import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Chip,
  Switch,
  FormControlLabel,
  Checkbox,
  List,
  ListItem
} from '@mui/material';
import {
  Add as AddIcon, Domain as DomainIcon, Palette as BrandingIcon,
  Email as EmailIcon, Security as ComplianceIcon, Refresh as RefreshIcon,
} from '@mui/icons-material';
import apiClient, { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';
import { useAuthStore } from '../stores/authStore';

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <Box hidden={value !== index} mt={2}>{value === index && children}</Box>
);

interface AdminUser {
  id: string;
  email: string;
  full_name: string;
  role_name?: string;
  is_active: boolean;
}

interface Role {
  id: string;
  name: string;
  permission_count?: number;
  permissions?: string[];
}

interface Location {
  id: string;
  name: string;
  address?: string;
  phone?: string;
}

interface SiteSettings {
  name: string;
  address?: string;
  phone?: string;
  email?: string;
}

interface Species {
  id: number;
  name: string;
  is_active: number;
}

interface AppointmentType {
  id: number;
  name: string;
  description?: string;
  color?: string;
  duration_minutes: number;
  is_active: number;
}

interface Branding {
  logo_url: string | null;
  primary_color: string;
  secondary_color: string;
  tagline: string | null;
  support_email: string | null;
  support_phone: string | null;
  footer_text: string | null;
}

interface EmailEntry {
  id: number;
  recipient_email: string;
  recipient_name: string | null;
  template: string;
  subject: string;
  status: string;
  attempts: number;
  failed_reason: string | null;
  created_at: string;
  sent_at: string | null;
}

interface AuditEntry {
  id: number;
  template: string;
  recipient_email: string;
  subject: string;
  status: string;
  sent_at: string | null;
  attempts: number;
  created_at: string;
}

const AdminPage = () => {
  const [tab, setTab] = useState(0);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [siteSettings, setSiteSettings] = useState<SiteSettings>({ name: '', address: '', phone: '', email: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // User invite dialog
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role_id: '' });
  const [inviteSaving, setInviteSaving] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Role permissions dialog
  const [roleDialog, setRoleDialog] = useState<Role | null>(null);
  const [allPermissions, setAllPermissions] = useState<string[]>([]);
  const [selectedPerms, setSelectedPerms] = useState<string[]>([]);
  const [permSaving, setPermSaving] = useState(false);

  // Location dialog
  const [locOpen, setLocOpen] = useState(false);
  const [locForm, setLocForm] = useState({ name: '', address: '', phone: '' });
  const [locSaving, setLocSaving] = useState(false);
  const [locError, setLocError] = useState<string | null>(null);

  // Master data state
  const [speciesList, setSpeciesList] = useState<Species[]>([]);
  const [apptTypes, setApptTypes] = useState<AppointmentType[]>([]);
  const [speciesDialogOpen, setSpeciesDialogOpen] = useState(false);
  const [newSpeciesName, setNewSpeciesName] = useState('');
  const [apptTypeDialogOpen, setApptTypeDialogOpen] = useState(false);
  const [newApptType, setNewApptType] = useState({ name: '', description: '', color: '#2196F3', duration_minutes: '30' });
  const [seedLoading, setSeedLoading] = useState(false);

  // Branding
  const [branding, setBranding] = useState<Branding>({
    logo_url: '', primary_color: '#1976d2', secondary_color: '#dc004e',
    tagline: '', support_email: '', support_phone: '', footer_text: '',
  });
  const [brandingSaving, setBrandingSaving] = useState(false);

  // Email queue
  const [emailQueue, setEmailQueue] = useState<EmailEntry[]>([]);
  const [emailStats, setEmailStats] = useState<Record<string, number>>({});
  const [emailFilter, setEmailFilter] = useState('');
  const [emailProcessing, setEmailProcessing] = useState(false);

  // Compliance
  const [complianceClientId, setComplianceClientId] = useState('');
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);
  const [eraseOpen, setEraseOpen] = useState(false);
  const [eraseReason, setEraseReason] = useState('');
  const [eraseLoading, setEraseLoading] = useState(false);

  // Super-admin dashboard
  const { user } = useAuthStore();
  const isSuperAdmin = user?.role_name === 'Super Admin' || user?.permissions?.includes('super_admin');
  const [superDash, setSuperDash] = useState<Record<string, unknown> | null>(null);
  const [superSites, setSuperSites] = useState<Record<string, unknown>[]>([]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(API_ENDPOINTS.ADMIN.USERS);
      setUsers(res.data || res || []);
    } catch { setError('Failed to load users.'); }
    finally { setLoading(false); }
  }, []);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(API_ENDPOINTS.ADMIN.ROLES);
      setRoles(res.data || res || []);
    } catch { setError('Failed to load roles.'); }
    finally { setLoading(false); }
  }, []);

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(API_ENDPOINTS.ADMIN.LOCATIONS);
      setLocations(res.data || res || []);
    } catch { setError('Failed to load locations.'); }
    finally { setLoading(false); }
  }, []);

  const fetchSiteSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(API_ENDPOINTS.ADMIN.SITE);
      const data = res.data || res;
      setSiteSettings({
        name: data.name || '',
        address: data.address || '',
        phone: data.phone || '',
        email: data.email || '',
      });
    } catch { setError('Failed to load site settings.'); }
    finally { setLoading(false); }
  }, []);

  const fetchMasterData = useCallback(async () => {
    try {
      const [specRes, apptRes] = await Promise.all([
        api.get(API_ENDPOINTS.ADMIN.SPECIES),
        api.get(API_ENDPOINTS.ADMIN.APPOINTMENT_TYPES),
      ]);
      setSpeciesList(specRes.data || []);
      setApptTypes(apptRes.data || []);
    } catch { setError('Failed to load master data.'); }
  }, []);

  const fetchBranding = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<Branding>(API_ENDPOINTS.ADMIN_BRANDING.GET);
      setBranding({
        logo_url: res.logo_url ?? '',
        primary_color: res.primary_color ?? '#1976d2',
        secondary_color: res.secondary_color ?? '#dc004e',
        tagline: res.tagline ?? '',
        support_email: res.support_email ?? '',
        support_phone: res.support_phone ?? '',
        footer_text: res.footer_text ?? '',
      });
    } catch { setError('Failed to load branding.'); }
    finally { setLoading(false); }
  }, []);

  const fetchEmailQueue = useCallback(async () => {
    setLoading(true);
    try {
      const [queueRes, statsRes] = await Promise.all([
        api.get<{ data: EmailEntry[] }>(emailFilter ? `${API_ENDPOINTS.EMAIL_QUEUE.LIST}?status=${emailFilter}` : API_ENDPOINTS.EMAIL_QUEUE.LIST),
        api.get<{ by_status: Record<string, number> }>(API_ENDPOINTS.EMAIL_QUEUE.STATS),
      ]);
      setEmailQueue(queueRes.data ?? []);
      setEmailStats(statsRes.by_status ?? {});
    } catch { setError('Failed to load email queue.'); }
    finally { setLoading(false); }
  }, [emailFilter]);

  const fetchAuditLog = useCallback(async () => {
    try {
      const res = await api.get<{ data: AuditEntry[] }>(API_ENDPOINTS.COMPLIANCE.AUDIT_LOG);
      setAuditLog(res.data ?? []);
    } catch { /* silently ignore */ }
  }, []);

  const fetchSuperDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, sitesRes] = await Promise.all([
        api.get<Record<string, unknown>>(API_ENDPOINTS.ADMIN_SUPER.DASHBOARD),
        api.get<{ data: Record<string, unknown>[] }>(API_ENDPOINTS.ADMIN_SUPER.SITES),
      ]);
      setSuperDash(dashRes);
      setSuperSites(sitesRes.data ?? []);
    } catch { setError('Failed to load super-admin dashboard.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (tab === 0) fetchUsers();
    if (tab === 1) fetchRoles();
    if (tab === 2) fetchSiteSettings();
    if (tab === 3) fetchLocations();
    if (tab === 4) fetchBranding();
    if (tab === 5) fetchEmailQueue();
    if (tab === 6) { fetchAuditLog(); }
    if (tab === 7) fetchMasterData();
    if (tab === 8 && isSuperAdmin) fetchSuperDashboard();
  }, [tab, fetchUsers, fetchRoles, fetchSiteSettings, fetchLocations, fetchBranding, fetchEmailQueue, fetchAuditLog, fetchMasterData, fetchSuperDashboard, isSuperAdmin]);

  const handleInviteUser = async () => {
    if (!inviteForm.email) { setInviteError('Email is required.'); return; }
    setInviteSaving(true);
    setInviteError(null);
    try {
      await api.post(API_ENDPOINTS.ADMIN.USERS, inviteForm);
      setSuccess('User invited successfully.');
      setInviteOpen(false);
      fetchUsers();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setInviteError(msg || 'Failed to invite user.');
    } finally {
      setInviteSaving(false);
    }
  };

  const toggleUserActive = async (user: AdminUser) => {
    try {
      await api.patch(`${API_ENDPOINTS.ADMIN.USERS}/${user.id}`, { is_active: !user.is_active });
      fetchUsers();
    } catch { setError('Failed to update user.'); }
  };

  const openRoleDialog = async (role: Role) => {
    setRoleDialog(role);
    setSelectedPerms(role.permissions || []);
    try {
      const res = await api.get(`${API_ENDPOINTS.ADMIN.ROLES}/permissions`);
      setAllPermissions(res.data || res || []);
    } catch {
      setAllPermissions([]);
    }
  };

  const togglePerm = (perm: string) => {
    setSelectedPerms((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );
  };

  const savePermissions = async () => {
    if (!roleDialog) return;
    setPermSaving(true);
    try {
      await api.put(`${API_ENDPOINTS.ADMIN.ROLES}/${roleDialog.id}/permissions`, { permissions: selectedPerms });
      setSuccess('Permissions saved.');
      setRoleDialog(null);
      fetchRoles();
    } catch { setError('Failed to save permissions.'); }
    finally { setPermSaving(false); }
  };

  const saveSiteSettings = async () => {
    try {
      await api.put(API_ENDPOINTS.ADMIN.SITE, siteSettings);
      setSuccess('Site settings saved.');
    } catch { setError('Failed to save site settings.'); }
  };

  const handleSeedMasterData = async () => {
    setSeedLoading(true);
    try {
      await api.post(API_ENDPOINTS.ADMIN.SEED_MASTER_DATA, {});
      setSuccess('Master data seeded successfully.');
      fetchMasterData();
    } catch { setError('Failed to seed master data.'); }
    finally { setSeedLoading(false); }
  };

  const handleAddSpecies = async () => {
    if (!newSpeciesName.trim()) return;
    try {
      await api.post(API_ENDPOINTS.ADMIN.SPECIES, { name: newSpeciesName.trim() });
      setSpeciesDialogOpen(false);
      setNewSpeciesName('');
      fetchMasterData();
    } catch { setError('Failed to add species.'); }
  };

  const handleAddApptType = async () => {
    if (!newApptType.name.trim()) return;
    try {
      await api.post(API_ENDPOINTS.ADMIN.APPOINTMENT_TYPES, {
        ...newApptType, duration_minutes: Number(newApptType.duration_minutes),
      });
      setApptTypeDialogOpen(false);
      setNewApptType({ name: '', description: '', color: '#2196F3', duration_minutes: '30' });
      fetchMasterData();
    } catch { setError('Failed to add appointment type.'); }
  };

  const addLocation = async () => {
    if (!locForm.name) { setLocError('Name is required.'); return; }
    setLocSaving(true);
    setLocError(null);
    try {
      await api.post(API_ENDPOINTS.ADMIN.LOCATIONS, locForm);
      setLocOpen(false);
      fetchLocations();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setLocError(msg || 'Failed to add location.');
    } finally {
      setLocSaving(false);
    }
  };

  const saveBranding = async () => {
    setBrandingSaving(true);
    try {
      await api.put(API_ENDPOINTS.ADMIN_BRANDING.UPDATE, branding);
      setSuccess('Branding saved.');
    } catch { setError('Failed to save branding.'); }
    finally { setBrandingSaving(false); }
  };

  const processEmailQueue = async () => {
    setEmailProcessing(true);
    try {
      const res = await api.post<{ processed: number; sent: number; failed: number }>(API_ENDPOINTS.EMAIL_QUEUE.PROCESS, {});
      setSuccess(`Processed ${res.processed} emails — Sent: ${res.sent}, Failed: ${res.failed}`);
      fetchEmailQueue();
    } catch { setError('Failed to process email queue.'); }
    finally { setEmailProcessing(false); }
  };

  const cancelEmail = async (id: number) => {
    try { await api.patch(API_ENDPOINTS.EMAIL_QUEUE.CANCEL(id), {}); fetchEmailQueue(); }
    catch { setError('Failed to cancel email.'); }
  };

  const retryEmail = async (id: number) => {
    try { await api.post(API_ENDPOINTS.EMAIL_QUEUE.RETRY(id), {}); fetchEmailQueue(); }
    catch { setError('Failed to retry email.'); }
  };

  const exportClientData = async () => {
    if (!complianceClientId) { setError('Enter a Client ID first.'); return; }
    const token = localStorage.getItem('access_token');
    const siteId = localStorage.getItem('site_id');
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://sw-vet-api.majorsmart.com';
    const res = await fetch(`${baseUrl}/api/v1${API_ENDPOINTS.COMPLIANCE.EXPORT(complianceClientId)}`, {
      headers: { Authorization: `Bearer ${token}`, 'X-Site-ID': siteId ?? '' },
    });
    if (!res.ok) { setError('Export failed — client not found or access denied.'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `client-${complianceClientId}-export.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const eraseClientData = async () => {
    if (!complianceClientId) return;
    setEraseLoading(true);
    try {
      await apiClient.delete(`/compliance/erase/${complianceClientId}`, {
        data: { confirm: true, reason: eraseReason || 'User request' },
      });
      setSuccess(`Client ${complianceClientId} PII erased.`);
      setEraseOpen(false);
      setEraseReason('');
    } catch { setError('Erase failed.'); }
    finally { setEraseLoading(false); }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Admin</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Paper>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Users" />
          <Tab label="Roles" />
          <Tab label="Site Settings" />
          <Tab label="Locations" />
          <Tab icon={<BrandingIcon />} iconPosition="start" label="Branding" />
          <Tab icon={<EmailIcon />} iconPosition="start" label="Email Queue" />
          <Tab icon={<ComplianceIcon />} iconPosition="start" label="Compliance" />
          <Tab label="Master Data" />
          {isSuperAdmin && <Tab icon={<DomainIcon />} iconPosition="start" label="Super Admin" />}
        </Tabs>

        {/* Users Tab */}
        <TabPanel value={tab} index={0}>
          <Box px={2} pb={2}>
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setInviteError(null); setInviteForm({ email: '', role_id: '' }); setInviteOpen(true); }}>
                Invite User
              </Button>
            </Box>
            {loading ? <CircularProgress /> : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Active</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.length === 0 ? (
                      <TableRow><TableCell colSpan={4} align="center">No users found</TableCell></TableRow>
                    ) : users.map((u) => (
                      <TableRow key={u.id}>
                        <TableCell>{u.full_name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>{u.role_name || '—'}</TableCell>
                        <TableCell>
                          <Switch checked={u.is_active} onChange={() => toggleUserActive(u)} size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>

        {/* Roles Tab */}
        <TabPanel value={tab} index={1}>
          <Box px={2} pb={2}>
            {loading ? <CircularProgress /> : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Role</TableCell>
                      <TableCell>Permissions</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {roles.length === 0 ? (
                      <TableRow><TableCell colSpan={3} align="center">No roles found</TableCell></TableRow>
                    ) : roles.map((role) => (
                      <TableRow key={role.id}>
                        <TableCell>{role.name}</TableCell>
                        <TableCell>
                          <Chip label={`${role.permission_count ?? role.permissions?.length ?? 0} permissions`} size="small" />
                        </TableCell>
                        <TableCell align="right">
                          <Button size="small" onClick={() => openRoleDialog(role)}>Manage</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>

        {/* Site Settings Tab */}
        <TabPanel value={tab} index={2}>
          <Box px={2} pb={2} maxWidth={480}>
            {loading ? <CircularProgress /> : (
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField label="Site Name" fullWidth value={siteSettings.name}
                  onChange={(e) => setSiteSettings((s) => ({ ...s, name: e.target.value }))} />
                <TextField label="Address" fullWidth value={siteSettings.address}
                  onChange={(e) => setSiteSettings((s) => ({ ...s, address: e.target.value }))} />
                <TextField label="Phone" fullWidth value={siteSettings.phone}
                  onChange={(e) => setSiteSettings((s) => ({ ...s, phone: e.target.value }))} />
                <TextField label="Email" fullWidth value={siteSettings.email}
                  onChange={(e) => setSiteSettings((s) => ({ ...s, email: e.target.value }))} />
                <Button variant="contained" onClick={saveSiteSettings} sx={{ alignSelf: 'flex-start' }}>
                  Save Settings
                </Button>
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Locations Tab */}
        <TabPanel value={tab} index={3}>
          <Box px={2} pb={2}>
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setLocError(null); setLocForm({ name: '', address: '', phone: '' }); setLocOpen(true); }}>
                Add Location
              </Button>
            </Box>
            {loading ? <CircularProgress /> : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell>Phone</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {locations.length === 0 ? (
                      <TableRow><TableCell colSpan={3} align="center">No locations found</TableCell></TableRow>
                    ) : locations.map((loc) => (
                      <TableRow key={loc.id}>
                        <TableCell>{loc.name}</TableCell>
                        <TableCell>{loc.address || '—'}</TableCell>
                        <TableCell>{loc.phone || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>
        {/* Branding Tab */}
        <TabPanel value={tab} index={4}>
          <Box px={2} pb={2} maxWidth={520}>
            {loading ? <CircularProgress /> : (
              <Box display="flex" flexDirection="column" gap={2}>
                <Typography variant="subtitle2" color="text.secondary">Customize your clinic's appearance</Typography>
                <TextField label="Logo URL" fullWidth size="small" value={branding.logo_url ?? ''}
                  onChange={e => setBranding(b => ({ ...b, logo_url: e.target.value }))} />
                <Box display="flex" gap={2}>
                  <TextField label="Primary Colour" type="color" size="small" sx={{ flex: 1 }}
                    value={branding.primary_color}
                    onChange={e => setBranding(b => ({ ...b, primary_color: e.target.value }))}
                    InputLabelProps={{ shrink: true }} />
                  <TextField label="Secondary Colour" type="color" size="small" sx={{ flex: 1 }}
                    value={branding.secondary_color}
                    onChange={e => setBranding(b => ({ ...b, secondary_color: e.target.value }))}
                    InputLabelProps={{ shrink: true }} />
                </Box>
                <TextField label="Tagline" fullWidth size="small" value={branding.tagline ?? ''}
                  onChange={e => setBranding(b => ({ ...b, tagline: e.target.value }))} />
                <TextField label="Support Email" fullWidth size="small" value={branding.support_email ?? ''}
                  onChange={e => setBranding(b => ({ ...b, support_email: e.target.value }))} />
                <TextField label="Support Phone" fullWidth size="small" value={branding.support_phone ?? ''}
                  onChange={e => setBranding(b => ({ ...b, support_phone: e.target.value }))} />
                <TextField label="Footer Text" fullWidth size="small" multiline rows={2}
                  value={branding.footer_text ?? ''}
                  onChange={e => setBranding(b => ({ ...b, footer_text: e.target.value }))} />
                <Button variant="contained" onClick={saveBranding} disabled={brandingSaving} sx={{ alignSelf: 'flex-start' }}>
                  {brandingSaving ? <CircularProgress size={20} /> : 'Save Branding'}
                </Button>
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Email Queue Tab */}
        <TabPanel value={tab} index={5}>
          <Box px={2} pb={2}>
            <Box display="flex" gap={2} mb={2} alignItems="center" flexWrap="wrap">
              <Box display="flex" gap={1} flexWrap="wrap">
                {Object.entries(emailStats).map(([status, count]) => (
                  <Chip key={status} label={`${status}: ${count}`} size="small"
                    color={status === 'failed' ? 'error' : status === 'sent' ? 'success' : status === 'pending' ? 'warning' : 'default'}
                    onClick={() => setEmailFilter(emailFilter === status ? '' : status)} variant={emailFilter === status ? 'filled' : 'outlined'} />
                ))}
              </Box>
              <Box ml="auto" display="flex" gap={1}>
                <Button size="small" startIcon={<RefreshIcon />} onClick={fetchEmailQueue}>Refresh</Button>
                <Button size="small" variant="contained" onClick={processEmailQueue} disabled={emailProcessing}>
                  {emailProcessing ? <CircularProgress size={18} /> : 'Process Now'}
                </Button>
              </Box>
            </Box>
            {loading ? <CircularProgress /> : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Recipient</TableCell>
                      <TableCell>Template</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Attempts</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {emailQueue.length === 0 ? (
                      <TableRow><TableCell colSpan={7} align="center">No emails found</TableCell></TableRow>
                    ) : emailQueue.map(e => (
                      <TableRow key={e.id}>
                        <TableCell>{e.id}</TableCell>
                        <TableCell>{e.recipient_email}</TableCell>
                        <TableCell>{e.template}</TableCell>
                        <TableCell>
                          <Chip label={e.status} size="small"
                            color={e.status === 'failed' ? 'error' : e.status === 'sent' ? 'success' : e.status === 'pending' ? 'warning' : 'default'} />
                        </TableCell>
                        <TableCell>{e.attempts}</TableCell>
                        <TableCell>{e.created_at.slice(0, 10)}</TableCell>
                        <TableCell>
                          {(e.status === 'pending' || e.status === 'processing') && (
                            <Button size="small" color="error" onClick={() => cancelEmail(e.id)}>Cancel</Button>
                          )}
                          {(e.status === 'failed' || e.status === 'cancelled') && (
                            <Button size="small" onClick={() => retryEmail(e.id)}>Retry</Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>

        {/* Compliance Tab */}
        <TabPanel value={tab} index={6}>
          <Box px={2} pb={2}>
            <Typography variant="subtitle2" color="text.secondary" mb={2}>
              GDPR Subject Access Request & Right to Erasure
            </Typography>
            <Box display="flex" gap={2} alignItems="center" mb={3} maxWidth={480}>
              <TextField label="Client ID" size="small" sx={{ width: 160 }}
                value={complianceClientId} onChange={e => setComplianceClientId(e.target.value)} />
              <Button variant="outlined" onClick={exportClientData} disabled={!complianceClientId}>
                Export Data (JSON)
              </Button>
              <Button variant="outlined" color="error" onClick={() => setEraseOpen(true)} disabled={!complianceClientId}>
                Erase PII
              </Button>
            </Box>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>Communication Audit Log</Typography>
            <Button size="small" startIcon={<RefreshIcon />} onClick={fetchAuditLog} sx={{ mb: 1 }}>Refresh</Button>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Template</TableCell>
                    <TableCell>Recipient</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Sent</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditLog.length === 0 ? (
                    <TableRow><TableCell colSpan={6} align="center">No audit entries</TableCell></TableRow>
                  ) : auditLog.map(a => (
                    <TableRow key={a.id}>
                      <TableCell>{a.id}</TableCell>
                      <TableCell>{a.template}</TableCell>
                      <TableCell>{a.recipient_email}</TableCell>
                      <TableCell>{a.subject}</TableCell>
                      <TableCell><Chip label={a.status} size="small" color={a.status === 'sent' ? 'success' : 'default'} /></TableCell>
                      <TableCell>{a.sent_at ? a.sent_at.slice(0, 10) : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>
      </Paper>

      {/* Erase PII Confirmation Dialog */}
      <Dialog open={eraseOpen} onClose={() => setEraseOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm PII Erasure</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This will permanently anonymise all personal data for Client ID <strong>{complianceClientId}</strong>. Clinical records are retained for legal purposes.
          </Alert>
          <TextField fullWidth size="small" label="Reason for erasure" multiline rows={2}
            value={eraseReason} onChange={e => setEraseReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEraseOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={eraseClientData} disabled={eraseLoading}>
            {eraseLoading ? <CircularProgress size={20} /> : 'Confirm Erase'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Invite User</DialogTitle>
        <DialogContent>
          {inviteError && <Alert severity="error" sx={{ mb: 2 }}>{inviteError}</Alert>}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Email *" type="email" fullWidth value={inviteForm.email}
              onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))} />
            <TextField select label="Role" fullWidth value={inviteForm.role_id}
              onChange={(e) => setInviteForm((f) => ({ ...f, role_id: e.target.value }))}>
              <MenuItem value="">-- Select Role --</MenuItem>
              {roles.map((r) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)} disabled={inviteSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleInviteUser} disabled={inviteSaving}>
            {inviteSaving ? <CircularProgress size={20} /> : 'Invite'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Role Permissions Dialog */}
      <Dialog open={!!roleDialog} onClose={() => setRoleDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Manage Permissions — {roleDialog?.name}</DialogTitle>
        <DialogContent>
          {allPermissions.length === 0 ? (
            <Typography color="textSecondary">No permissions available or loading...</Typography>
          ) : (
            <List dense>
              {allPermissions.map((perm) => (
                <ListItem key={perm} disablePadding>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedPerms.includes(perm)}
                        onChange={() => togglePerm(perm)}
                        size="small"
                      />
                    }
                    label={perm}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialog(null)} disabled={permSaving}>Cancel</Button>
          <Button variant="contained" onClick={savePermissions} disabled={permSaving}>
            {permSaving ? <CircularProgress size={20} /> : 'Save Permissions'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Master Data Tab */}
      <TabPanel value={tab} index={7}>
        <Box px={2} pb={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Tenant Master Data</Typography>
            <Button
              variant="outlined" color="secondary"
              onClick={handleSeedMasterData} disabled={seedLoading}
            >
              {seedLoading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
              Seed Defaults
            </Button>
          </Box>

          {/* Species */}
          <Box mb={4}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1" fontWeight={700}>Species ({speciesList.length})</Typography>
              <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setSpeciesDialogOpen(true)}>
                Add Species
              </Button>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {speciesList.map(s => (
                <Chip key={s.id} label={s.name} color={s.is_active ? 'primary' : 'default'} variant="outlined" />
              ))}
              {speciesList.length === 0 && (
                <Alert severity="warning" sx={{ width: '100%' }}>
                  No species found. Click "Seed Defaults" to populate standard species and breeds.
                </Alert>
              )}
            </Box>
          </Box>

          {/* Appointment Types */}
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1" fontWeight={700}>Appointment Types ({apptTypes.length})</Typography>
              <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setApptTypeDialogOpen(true)}>
                Add Type
              </Button>
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Color</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Duration (min)</TableCell>
                    <TableCell>Active</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {apptTypes.length === 0 ? (
                    <TableRow><TableCell colSpan={5} align="center">No appointment types — click Seed Defaults</TableCell></TableRow>
                  ) : apptTypes.map(t => (
                    <TableRow key={t.id}>
                      <TableCell>
                        <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: t.color ?? '#2196F3', display: 'inline-block' }} />
                      </TableCell>
                      <TableCell>{t.name}</TableCell>
                      <TableCell>{t.description ?? '—'}</TableCell>
                      <TableCell align="right">{t.duration_minutes}</TableCell>
                      <TableCell>
                        <Chip size="small" label={t.is_active ? 'Active' : 'Inactive'} color={t.is_active ? 'success' : 'default'} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </TabPanel>

      {/* Add Species Dialog */}
      <Dialog open={speciesDialogOpen} onClose={() => setSpeciesDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Species</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Species Name" value={newSpeciesName}
            onChange={e => setNewSpeciesName(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSpeciesDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddSpecies} disabled={!newSpeciesName.trim()}>Add</Button>
        </DialogActions>
      </Dialog>

      {/* Add Appointment Type Dialog */}
      <Dialog open={apptTypeDialogOpen} onClose={() => setApptTypeDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Appointment Type</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField fullWidth label="Name *" value={newApptType.name}
              onChange={e => setNewApptType(p => ({ ...p, name: e.target.value }))} />
            <TextField fullWidth label="Description" value={newApptType.description}
              onChange={e => setNewApptType(p => ({ ...p, description: e.target.value }))} />
            <TextField fullWidth label="Color (hex)" value={newApptType.color}
              onChange={e => setNewApptType(p => ({ ...p, color: e.target.value }))} />
            <TextField fullWidth label="Duration (minutes)" type="number" value={newApptType.duration_minutes}
              onChange={e => setNewApptType(p => ({ ...p, duration_minutes: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApptTypeDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddApptType} disabled={!newApptType.name.trim()}>Add</Button>
        </DialogActions>
      </Dialog>

      {/* Super Admin Dashboard */}
      {isSuperAdmin && (
        <TabPanel value={tab} index={8}>
          <Box px={2} pb={2}>
            {loading ? <CircularProgress /> : superDash && (() => {
              const t = superDash.totals as Record<string, number> | undefined;
              const s = superDash.sites as Record<string, number> | undefined;
              const months = (superDash.revenue_by_month as { month: string; revenue: number }[]) ?? [];
              const sites = superSites;
              const fmt = (n: number | null | undefined) => n != null ? `RM ${Number(n).toFixed(0)}` : 'RM 0';
              return (
                <Box>
                  {/* KPI cards */}
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                    {[
                      { label: 'Active Sites', value: String(s?.active ?? 0) },
                      { label: 'Total Users', value: String(t?.total_users ?? 0) },
                      { label: 'Total Clients', value: String(t?.total_clients ?? 0) },
                      { label: 'Total Patients', value: String(t?.total_patients ?? 0) },
                      { label: "Today's Appointments", value: String(t?.today_appointments ?? 0) },
                      { label: 'Total Revenue', value: fmt(t?.total_revenue) },
                      { label: 'This Month', value: fmt(t?.this_month_revenue) },
                    ].map(kpi => (
                      <Paper key={kpi.label} variant="outlined" sx={{ p: 2, minWidth: 140, textAlign: 'center' }}>
                        <Typography variant="h6" color="primary">{kpi.value}</Typography>
                        <Typography variant="caption" color="text.secondary">{kpi.label}</Typography>
                      </Paper>
                    ))}
                  </Box>

                  {/* Revenue trend */}
                  {months.length > 0 && (
                    <Box mb={3}>
                      <Typography variant="subtitle1" fontWeight={600} mb={1}>Monthly Revenue (All Sites)</Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead><TableRow><TableCell>Month</TableCell><TableCell align="right">Revenue</TableCell><TableCell align="right">Invoices</TableCell></TableRow></TableHead>
                          <TableBody>
                            {months.map((m) => (
                              <TableRow key={m.month}>
                                <TableCell>{m.month}</TableCell>
                                <TableCell align="right">{fmt(m.revenue)}</TableCell>
                                <TableCell align="right">{(m as Record<string, unknown>).invoice_count as number}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}

                  {/* Top sites table */}
                  <Typography variant="subtitle1" fontWeight={600} mb={1}>Sites by Revenue</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Site</TableCell>
                          <TableCell>Plan</TableCell>
                          <TableCell align="right">Clients</TableCell>
                          <TableCell align="right">Patients</TableCell>
                          <TableCell align="right">Appointments</TableCell>
                          <TableCell align="right">Revenue</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sites.map((site) => (
                          <TableRow key={String(site.id)} hover>
                            <TableCell><strong>{String(site.name)}</strong></TableCell>
                            <TableCell><Chip size="small" label={String(site.subscription_plan ?? 'starter')} /></TableCell>
                            <TableCell align="right">{String(site.client_count ?? 0)}</TableCell>
                            <TableCell align="right">{String(site.patient_count ?? 0)}</TableCell>
                            <TableCell align="right">{String(site.appointment_count ?? 0)}</TableCell>
                            <TableCell align="right">{fmt(site.total_revenue as number)}</TableCell>
                            <TableCell><Chip size="small" color={site.is_active ? 'success' : 'default'} label={site.is_active ? 'Active' : 'Inactive'} /></TableCell>
                          </TableRow>
                        ))}
                        {sites.length === 0 && (
                          <TableRow><TableCell colSpan={7} align="center">No sites found</TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              );
            })()}
          </Box>
        </TabPanel>
      )}

      {/* Add Location Dialog */}
      <Dialog open={locOpen} onClose={() => setLocOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Location</DialogTitle>
        <DialogContent>
          {locError && <Alert severity="error" sx={{ mb: 2 }}>{locError}</Alert>}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Name *" fullWidth value={locForm.name}
              onChange={(e) => setLocForm((f) => ({ ...f, name: e.target.value }))} />
            <TextField label="Address" fullWidth value={locForm.address}
              onChange={(e) => setLocForm((f) => ({ ...f, address: e.target.value }))} />
            <TextField label="Phone" fullWidth value={locForm.phone}
              onChange={(e) => setLocForm((f) => ({ ...f, phone: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLocOpen(false)} disabled={locSaving}>Cancel</Button>
          <Button variant="contained" onClick={addLocation} disabled={locSaving}>
            {locSaving ? <CircularProgress size={20} /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPage;
